<template>
	<div v-if="customer && isUserAllowedInLevel5or6" class="relative flex flex-col h-full">
		<BaseHeader :title="$t('customer_file').toString()" fontawesome-title="user" />
		<div class="text-left">
			<div class="flex flex-wrap space-x-6 my-4 mx-16">
				<div class="text-2xl flex-wrap font-bold">{{ $t('core_data').toString() }}</div>
				<div
					class="bg-sp btn block btn-overview-edit flex items-center justify-center text-white"
					@click="$router.push({ name: 'management.customer.edit', params: { customer: customer.id, backlink: $route.path } })"
				>
					<font-awesome-icon :icon="['fas', 'edit']"></font-awesome-icon>
				</div>
			</div>
			<div class="mx-8 px-8 py-4 mb-4 bg-gray-300 rounded flex justify-between">
				<div class="flex flex-grow justify-between pt-2 pb-2">
					<div class="w-2/5">
						<div class="font-bold">{{ $t('customer') }}</div>
						<div class="flex mt-2" v-if="customer.lastName != null || customer.company.name != null">
							<div class="mr-2 w-6">
								<font-awesome-icon :icon="['fas', 'user']" class="text-gray-500" />
							</div>
							<div>
								<div v-if="isCustomerCompany">{{ $t('company') }} {{ customer.company.name }}</div>
								<div v-if="customer.lastName != null">{{ customerName }}</div>
							</div>
						</div>
						<div class="mt-2">
							<div class="flex">
								<div class="mr-2 w-6">
									<font-awesome-icon :icon="['fas', 'home']" class="text-gray-500" />
								</div>
								<div v-if="customer.street != null">
									{{ customer.street }} {{ customer.houseNumber }}<br />{{ customer.city.zipCode }}
									{{ customer.city.name }}
								</div>
								<div v-else>
									{{ $t('po_box') }} {{ customer.poBox }}<br />{{ customer.city.zipCode }}
									{{ customer.city.name }}
								</div>
							</div>

							<div class="flex mt-2" v-if="customer.phoneNumber">
								<div class="mr-2 w-6">
									<font-awesome-icon :icon="['fas', 'phone']" class="text-gray-500" />
								</div>
								<div>{{ customer.phoneNumber }}</div>
							</div>
							<div class="flex mt-2" v-if="customer.email">
								<div class="mr-2 w-6">
									<font-awesome-icon :icon="['fas', 'envelope']" class="text-gray-500" />
								</div>
								<div>{{ customer.email }}</div>
							</div>
						</div>
					</div>
					<div v-if="customer.consultant != null" class="w-2/5">
						<div class="font-bold">{{ $t('customer_consultant') }}</div>
						<div class="flex mt-2" v-if="customer.consultant.first_name && customer.consultant.last_name">
							<div class="mr-2 w-6">
								<font-awesome-icon :icon="['fas', 'user']" class="text-gray-500" />
							</div>
							<div>{{ customer.consultant.first_name }} {{ customer.consultant.last_name }}</div>
						</div>
						<div class="flex mt-2" v-if="customer.consultant.email">
							<div class="mr-2 w-6">
								<font-awesome-icon :icon="['fas', 'envelope']" class="text-gray-500" />
							</div>
							<div>{{ customer.consultant.email }}</div>
						</div>
					</div>
					<div class="w-1/5 justify-end" v-if="isUserAllowedInLevel5or6">
						<div class="font-bold">{{ $t('customer_app') }}</div>
						<div class="flex mt-2 cursor-pointer" @click="$router.push({ name: 'management.customer.overview.show_qr' })">
							<div class="mr-2 w-6">
								<font-awesome-icon :icon="['fas', 'qrcode']" class="text-gray-500" />
							</div>
							<div>{{ $t('show_qr_code') }}</div>
						</div>
						<div
							v-if="customer.customer_main != null"
							class="flex mt-2 cursor-pointer"
							@click="$router.push({ name: 'management.customer.overview.set_permission' })"
						>
							<div class="mr-2 w-6">
								<font-awesome-icon :icon="['fas', 'images']" class="text-gray-500" />
							</div>
							<div>{{ $t('set_media_perms') }}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="text-left m-8">
			<hr />
		</div>
		<div class="mx-16 text-left"></div>
		<div class="text-left">
			<div class="flex justify-between mx-16">
				<div class="flex flex-shrink space-x-6 grid grid-cols-3">
					<div class="text-2xl font-bold">{{ $t('car_fleet') }}</div>
					<div
						class="bg-sp btn block btn-overview-edit flex items-center justify-center text-white"
						@click="$router.push({ name: 'management.process.create_vehicle', params: { customer: customer.id, backlink: $route.path } })"
					>
						<font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
					</div>
				</div>
				<div class="flex justify-center">
					<BaseCheckbox
						:label="$t('all_vehicles')"
						class="mr-8 mb-0 pt-3"
						:filter-only="true"
						v-model="vehicleCheck.all"
						@input="toggleVehicleCheckbox('all', $event)"
					></BaseCheckbox>
					<BaseCheckbox
						:label="$t('active_vehicles')"
						fontawesome-before-label="car-crash"
						class="mr-8 mb-0 pt-3"
						:filter-only="true"
						v-model="vehicleCheck.active"
						@input="toggleVehicleCheckbox('active', $event)"
					></BaseCheckbox>
					<BaseCheckbox
						:label="$t('inactive_vehicles')"
						fontawesome-before-label="car-crash"
						class="mr-8 mb-0 pt-3"
						:filter-only="true"
						v-model="vehicleCheck.deactivated"
						@input="toggleVehicleCheckbox('deactivated', $event)"
					></BaseCheckbox>
				</div>
				<div class="flex">
					<div class="block btn-overview-edit flex items-center">
						<font-awesome-icon
							:icon="['fas', isVehicleCardEnabled ? 'angle-up' : 'angle-down']"
							@click="toggleVehicleCard"
							class="cursor-pointer text-2xl mr-1"
						/>
					</div>
				</div>
			</div>
			<div v-if="vehicles.length > 0 && isVehicleCardEnabled">
				<div
					class="my-4 mx-8 rounded even:bg-gray-100 odd:bg-gray-300"
					:class="activeVehicle === vehicle.id ? 'border-blue-700 border' : 'border'"
					v-for="vehicle in vehicles"
					:key="vehicle.id"
				>
					<div
						class="py-4 px-8"
						@click="
							$router.push({
								name: 'management.customer.vehicles.edit',
								params: { customer: customer.id, vehicleId: vehicle.id, backlink: $route.path },
							})
						"
					>
						<div class="flex w-full justify-between">
							<div class="cursor-pointer flex-grow">
								<div class="flex flex-wrap">
									<div class="w-1/4 font-bold">{{ $t('vehicle_input') }}</div>
									<div class="font-bold">{{ vehicle.manufacturer }} {{ vehicle.model }}</div>
								</div>
								<div class="flex flex-wrap">
									<div class="w-1/4 font-bold">{{ $t('vehicle_license_plate_input') }}</div>
									<div class="">{{ vehicle.license_plate }}</div>
								</div>
								<div class="flex flex-wrap">
									<div class="w-1/4 font-bold">{{ $t('vehicle_fin_input') }}</div>
									<div class="">{{ vehicle.vin }}</div>
								</div>
							</div>
							<div class="cursor-pointer flex flex-wrap justify-end" style="align-items: end" v-if="isAppContainer">
								<div class="flex my-4 justify-center" style="align-items: center">
									<BaseButton
										@click.stop="
											$router.push({
												name: 'management.customer.overview.process_info',
												query: { customer_id: customer.id, vehicle_id: vehicle.id },
											})
										"
									>
										<font-awesome-icon :icon="['fas', 'plus']" />
										{{ $t('add_process') }}
									</BaseButton>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-else-if="isVehicleCardEnabled">
				<div class="my-4 mx-8 rounded bg-gray-200">
					<div class="py-4 px-8">
						<div class="font-bold">
							{{ $t('no_vehicles') }}
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="text-left m-8">
			<hr />
		</div>
		<div class="text-left">
			<div class="flex justify-between mx-16">
				<div class="flex">
					<div class="text-2xl font-bold">{{ $t('process_overview') }}</div>
				</div>
				<div class="flex justify-center mr-6">
					<BaseCheckbox
						:filter-only="true"
						label="Alle Vorgänge"
						class="mr-8 mb-0 pt-3"
						v-model="check.all"
						@input="toggleProcessCheckbox('all', $event)"
					></BaseCheckbox>
					<BaseCheckbox
						:label="$t('damage_case')"
						fontawesome-before-label="car-crash"
						class="mr-8 mb-0 pt-3"
						:filter-only="true"
						v-model="check.damage"
						@input="toggleProcessCheckbox('damage', $event)"
					></BaseCheckbox>
					<BaseCheckbox
						:label="$t('order_documentation')"
						fontawesome-before-label="cog"
						class="mb-0 pt-3"
						:filter-only="true"
						v-model="check.order"
						@input="toggleProcessCheckbox('order_documentation', $event)"
					></BaseCheckbox>
				</div>
				<div class="flex justify-end">
					<div class="block btn-overview-edit flex items-center justify-center">
						<font-awesome-icon
							:icon="['fas', isProcessCardEnabled ? 'angle-up' : 'angle-down']"
							@click="toggleProcessCard"
							class="cursor-pointer text-2xl mr-1"
						/>
					</div>
				</div>
			</div>
			<CustomerProcessList
				class="my-4 mx-8"
				:processes="processes"
				:customer="customer"
				v-if="processes.length > 0 && customer && isProcessCardEnabled"
			/>
			<div v-else-if="isProcessCardEnabled">
				<div class="my-4 mx-8 rounded bg-gray-200">
					<div class="py-4 px-8">
						<div class="font-bold">
							{{ $t('no_processes_found') }}
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="text-left m-8">
			<hr />
		</div>
		<div class="mx-16 text-left"></div>
		<div class="text-left">
			<div class="flex justify-between mx-16">
				<div class="flex flex-wrap space-x-6 grid grid-cols-4">
					<div class="text-2xl font-bold">{{ $t('chats') }}</div>
					<div class="bg-sp btn block btn-overview-edit flex items-center justify-center text-white" @click="startChat">
						<font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
					</div>
				</div>
				<div class="flex">
					<div class="block btn-overview-edit flex items-center justify-center">
						<font-awesome-icon
							:icon="['fas', isChatCardEnabled ? 'angle-up' : 'angle-down']"
							@click="toggleChatCard"
							class="cursor-pointer text-2xl mr-1"
						/>
					</div>
				</div>
			</div>
			<div v-if="chats.length > 0 && isChatCardEnabled" class="cursor-pointer">
				<div class="my-4 mx-8 rounded even:bg-gray-100 odd:bg-gray-300" v-for="chat in chats" :key="chat.id">
					<div class="py-4 px-8" @click="$router.push({ name: 'repair_shop.chats_detail', params: { id: chat.id } })">
						<div class="grid mt-1 mb-1">
							<div>
								<div class="flex w-full space-x-4">
									<div class="w-4/12 font-bold truncate">
										{{
											chat.subject === 'general'
												? $t('general_title')
												: getDamageCaseLabel(chat.damage_case.type, chat.damage_case.license_plate, getGermanDate(chat.damage_case.created_at))
										}}
									</div>
									<div class="w-2/10 font-bold truncate">{{ formatDate(chat.last_message.created_at) }}</div>
									<div class="w-3/10 font-bold truncate">
										{{ getChatContent(chat.last_message) }}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-else-if="isChatCardEnabled">
				<div class="my-4 mx-8 rounded even:bg-gray-100 odd:bg-gray-300">
					<div class="py-4 px-8">
						<div class="font-bold">
							{{ $t('no_messages_sent') }}
						</div>
					</div>
				</div>
			</div>
		</div>
		<router-view />
		<Footer
			@backButtonClick="$router.push({ path: '/management' })"
			:is-continue-button="false"
			:is-skippable="false"
			:is-back-button="true"
			class="mx-8"
		>
		</Footer>
	</div>
	<Forbidden v-else-if="!isLoading" />
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import CustomerProcessList from '@/views/management/Customer/CustomerProcessList';
import { ChatsApiFactory, CustomersApiFactory, VehiclesApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import BaseCheckbox from '@/components/forms/inputs/BaseCheckbox';
import BaseButton from '@/components/forms/buttons/BaseButton';
import Footer from '@/components/damagecases/Footer';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import Authentication from '@/mixins/Authentication';
import {getDamageCaseLabel, getGermanDate, getGermanDateTime} from '@/plugins/Utils';
import VueQrcode from 'vue-qrcode';
import Forbidden from '@/views/default/Forbidden.vue';

export default {
	name: 'CustomerOverview',
	components: { Forbidden, Footer, BaseButton, BaseCheckbox, CustomerProcessList, BaseHeader, VueQrcode },
	mixins: [AppContainerMixin, Authentication],
	data() {
		return {
			activeProcessCheckbox: 'all',
			customer: null,
			vehicles: [],
			isVehicleCardEnabled: true,
			isProcessCardEnabled: true,
			isChatCardEnabled: true,
			activeVehicle: null,
			processes: [],
			chats: [],
			vehicleCheck: { all: true, active: false, deactivated: false },
			check: { all: true, damage: false, order: false },
			isLoading: true,
		};
	},
	computed: {
		getQrLink() {
			return 'imd-connect-app://id=' + this.customer.id + '&qrkey=' + this.customer.qrKey;
		},
		isCustomerCompany() {
			return this.customer?.gender === 'company';
		},
		customerName() {
			return this.customer ? `${this.customer?.firstName} ${this.customer?.lastName}` : this.$t('customer');
		},
	},
	mounted() {
		if (this.isUserAllowedInLevel5or6) {
			this.getCustomer();

			this.getCustomerVehicles();

			this.$root.$on('updateChats', () => {
				this.loadChats();
			});
			this.$root.$on('updateCustomer', () => {
				this.getCustomer();
			});
		}
	},
	methods: {
    getGermanDate,
		getDamageCaseLabel,
		getCustomer() {
			CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.customersIdGet(this.$route.params.customer)
				.then(response => {
					this.customer = response.data;
					this.$store.commit('setCustomer', response.data);
				})
				.then(a => {
					this.loadProcesses('', '');
					this.loadChats();
					this.isLoading = false;
				});
		},
		startChat() {
			if (this.customer.customer_main != null) {
				this.$router.push({ name: 'management.customer.overview.create_chat', params: { customerid: this.customer.id } });
			} else {
				this.$swal(this.$t('general.error').toString(), this.$t('customer_did_not_install_app').toString(), 'error');
			}
		},
		getChatContent(lastMessage) {
			switch (lastMessage.type) {
				case 'image':
					return this.$t('image');
				case 'video':
					return this.$t('video');
				case 'document':
					return this.$t('document');
				default:
					return lastMessage.content;
			}
		},
		getCustomerVehicles(filter) {
			VehiclesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.vehiclesCustomerIdGet(this.$route.params.customer, filter)
				.then(response => {
					this.vehicles = response.data;
				});
		},
		toggleVehicle(vehicleId) {
			if (this.activeVehicle === null) {
				this.activeVehicle = vehicleId;
			} else if (this.activeVehicle === vehicleId) {
				this.activeVehicle = null;
				vehicleId = '';
			} else {
				this.activeVehicle = vehicleId;
			}
			this.check.all = true;
			this.check.damage = false;
			this.check.order = false;

			this.loadProcesses(vehicleId, '');
		},
		loadProcesses(vehicleId, type) {
			CustomersApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.customersIdProcessesGet(this.customer.id, vehicleId, type)
				.then(response => {
					this.processes = response.data.data;
					this.$store.commit('setDamageCases', response.data.data);
				});
		},
		loadChats() {
			ChatsApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.chatsGet(1, 25, this.customer.id)
				.then(response => {
					this.chats = response.data.data;
				})
				.catch(error => {
					console.log(error);
				});
		},
		formatDate(timestamp) {
			return getGermanDateTime(timestamp);
		},
		toggleProcessCheckbox(type, event) {
			if (type === 'all') {
				this.loadProcesses('', '');
				this.check.all = true;
				this.check.damage = false;
				this.check.order = false;
			} else if (type === 'order_documentation') {
				this.loadProcesses('', 'order_documentation');
				this.check.all = false;
				this.check.damage = false;
				this.check.order = true;
			} else if (type === 'damage') {
				this.loadProcesses('', 'damage');
				this.check.all = false;
				this.check.damage = true;
				this.check.order = false;
			}
		},
		toggleVehicleCheckbox(type, event) {
			if (type === 'all') {
				this.getCustomerVehicles();
				this.vehicleCheck.all = true;
				this.vehicleCheck.active = false;
				this.vehicleCheck.deactivated = false;
			} else if (type === 'deactivated') {
				this.getCustomerVehicles('deactivated');
				this.vehicleCheck.all = false;
				this.vehicleCheck.active = false;
				this.vehicleCheck.deactivated = true;
			} else if (type === 'active') {
				this.getCustomerVehicles('active');
				this.vehicleCheck.all = false;
				this.vehicleCheck.active = true;
				this.vehicleCheck.deactivated = false;
			}
		},
		toggleVehicleCard() {
			this.isVehicleCardEnabled = !this.isVehicleCardEnabled;
		},
		toggleProcessCard() {
			this.isProcessCardEnabled = !this.isProcessCardEnabled;
		},
		toggleChatCard() {
			this.isChatCardEnabled = !this.isChatCardEnabled;
		},
	},
};
</script>

<style scoped></style>
